import React, { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface RecoveryGuardProps {
  children: React.ReactNode;
}

/**
 * Recovery Guard Component
 * 
 * Prevents users from accessing the app while in recovery mode.
 * If a user has a recovery session but tries to navigate to any page,
 * they will be redirected back to the password reset modal.
 */
const RecoveryGuard: React.FC<RecoveryGuardProps> = ({ children }) => {
  const { isInRecoveryMode, showPasswordRecoveryForm, setShowPasswordRecoveryForm } = useAuth();

  useEffect(() => {
    // Check for recovery session on every navigation
    const recoverySession = localStorage.getItem('cycling-buddy-recovery-session');
    if (recoverySession && !showPasswordRecoveryForm) {
      // Force user back to password reset modal
      setShowPasswordRecoveryForm(true);
    }
  }, [showPasswordRecoveryForm, setShowPasswordRecoveryForm]);

  // If in recovery mode, don't render the main app content
  // The password recovery modal will be shown by the AuthContext
  if (isInRecoveryMode) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔐</div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            Password Reset Required
          </h2>
          <p className="text-gray-600">
            You must complete the password reset process to continue.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default RecoveryGuard;
